import { useDocumentVisibility } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { IAlertSummary } from '@/types/Alert'

/*
 * How many alerts are open — one number for the whole console, polled so the menu
 * badge is right without anybody opening the alerts page.
 *
 * IT STARTS AT NOTHING AND CAN ONLY ADD A BADGE. Every failure is swallowed: no toast,
 * no console line, no Sentry. A fleet-api deployed before this feature answers 404 to
 * /alerts/summary, and the console then looks exactly as it did before this store
 * existed — which is the whole contract. An alerting console that broke the menu of a
 * console with no alerting would be a worse bargain than no badge.
 *
 * It polls on a minute, and only while the tab is actually being looked at: a console
 * left open on a wall overnight has nobody to tell, and a request a minute for
 * fourteen hours to answer a question nobody is asking is not free on the server that
 * also runs four other stacks.
 */
const POLL_MS = 60_000

const emptySummary = (): IAlertSummary => ({ open: 0, critical: 0 })

export const useAlertsStore = defineStore('alerts', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const visibility = useDocumentVisibility()

    const summary = ref<IAlertSummary>(emptySummary())

    let timer: ReturnType<typeof setInterval> | undefined

    /** The badge's colour: one critical alert among ten warnings is still critical. */
    const badgeColour = computed<'error' | 'warning'>(() => summary.value.critical > 0 ? 'error' : 'warning')

    const load = async () => {
        if (!appStore.isLoggedIn()) return

        const { statusCode, data } = await fetchStore.fetch('/alerts/summary').get().json<IAlertSummary>()

        if (statusCode.value !== 200 || !data.value) return

        summary.value = { ...emptySummary(), ...data.value }
    }

    const stopTimer = () => {
        if (timer) clearInterval(timer)

        timer = undefined
    }

    const start = () => {
        if (timer) return

        void load()

        timer = setInterval(() => {
            if (visibility.value === 'hidden') return

            void load()
        }, POLL_MS)
    }

    const reset = () => {
        stopTimer()
        summary.value = emptySummary()
    }

    /*
     * A tab that has been in the background for an hour is looking at an hour-old
     * number the moment somebody switches back to it. Asking again on the way in is
     * cheaper than polling a hidden tab, and is what makes the badge trustworthy.
     */
    watch(visibility, (state) => {
        if (state === 'visible') void load()
    })

    /** Signing out throws the count away, like every other per-session state. */
    watch(() => appStore.JWT, (jwt) => {
        if (jwt) {
            start()
        } else {
            reset()
        }
    })

    return {
        summary,
        badgeColour,
        load, start, reset,
    }
})
